import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import type { PontoMensal } from '../types';

interface Props {
  pontos: PontoMensal[]; // já ordenados cronologicamente, no máx. 13 meses (fatiar antes de passar)
  modoCompacto?: boolean; // versão mini-card: ~80px, sem eixos/legenda, só as barras + número-chave
}

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 });

const TooltipCustom = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl p-3 shadow-xl text-xs">
      <p className="font-bold text-gray-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">{p.name}: {fmtBRL(p.value)}</p>
      ))}
    </div>
  );
};

const EvolucaoMensalChart: React.FC<Props> = ({ pontos, modoCompacto = false }) => {
  if (modoCompacto) {
    const ultimo = pontos[pontos.length - 1] ?? null;
    return (
      <div>
        <h3 className="text-xs font-bold text-gray-800 dark:text-white mb-1 truncate">Evolução Financeira Mensal</h3>
        <p className="text-sm font-bold text-gray-900 dark:text-white mb-1 truncate">
          {ultimo ? fmtBRL(ultimo.emitido) : '—'}{' '}
          <span className="text-[10px] font-normal text-gray-400">emitido{ultimo ? ` em ${ultimo.mes}` : ''}</span>
        </p>
        <ResponsiveContainer width="100%" height={70}>
          <BarChart data={pontos} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
            <Bar dataKey="emitido" fill="#6b7280" radius={[2, 2, 0, 0]} />
            <Bar dataKey="pago" fill="#5F6C37" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3">Evolução Financeira Mensal</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={pontos} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#9ca3af" />
          <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => fmtBRL(v)} width={70} />
          <Tooltip content={<TooltipCustom />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="emitido" name="Emitido" fill="#6b7280" radius={[4, 4, 0, 0]} />
          <Bar dataKey="pago" name="Pago" fill="#5F6C37" radius={[4, 4, 0, 0]} />
          <Bar dataKey="emAberto" name="Em aberto" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          <Bar dataKey="vencido" name="Vencido" fill="#dc2626" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EvolucaoMensalChart;
